import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { EditModeProvider } from './EditModeContext';
import { AuthProvider } from './AuthContext';
import ProtectedRoute from './components/ProtectedRoute';

// ⚡ Only Home page loads immediately (first page users see)
import Home from './Home';

// 🔄 All other pages load on-demand (code splitting)
const Contact = lazy(() => import('./Contact'));
const About = lazy(() => import('./About'));
const Blog = lazy(() => import('./Blog'));
const BlogPost = lazy(() => import('./BlogPost'));
const Theme = lazy(() => import('./theme'));
const Login = lazy(() => import('./Login'));
const Dashboard = lazy(() => import('./Dashboard'));
const PrivacyPolicy = lazy(() => import('./PrivacyPolicy'));
const RefundPolicy = lazy(() => import('./RefundPolicy'));
const CreditCardSecurity = lazy(() => import('./CreditCardSecurity'));
const TermsConditions = lazy(() => import('./TermsConditions'));
const Support = lazy(() => import('./Support'));
const ServiceTemplate = lazy(() => import('./ServiceTemplate'));
const DynamicServicePage = lazy(() => import('./DynamicServicePage'));
const BgTest = lazy(() => import('./BgTest'));
const BlogPostEditor = lazy(() => import('./BlogPostEditor'));
const BlogPreviewEditor = lazy(() => import('./BlogPreviewEditor'));

// Loading fallback component
const PageLoader = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    fontSize: '18px',
    color: '#395173',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  }}>
    Loading...
  </div>
);

function App() {
  return (
    <Router>
      <AuthProvider>
        <EditModeProvider>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<Navigate to="/home" replace />} />
              <Route path="/home" element={<Home />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/about" element={<About />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/sample" element={<BlogPost />} />
              <Route path="/blog/:postId" element={<BlogPost />} />
              <Route path="/theme" element={<Theme />} />
              <Route path="/login" element={<Login />} />
              <Route path="/medioslogin" element={<Login />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/refund-policy" element={<RefundPolicy />} />
              <Route path="/credit-card-security" element={<CreditCardSecurity />} />
              <Route path="/terms-conditions" element={<TermsConditions />} />
              <Route path="/support" element={<Support />} />
              <Route path="/service-template" element={<ServiceTemplate />} />
              <Route path="/bgTest" element={<BgTest />} />

              {/* Protected routes - require editor or admin role */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute requireRole="editor">
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/blog/new"
                element={
                  <ProtectedRoute requireRole="editor">
                    <BlogPostEditor />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/blog/edit/:postId"
                element={
                  <ProtectedRoute requireRole="editor">
                    <BlogPostEditor />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/blog/preview/new"
                element={
                  <ProtectedRoute requireRole="editor">
                    <BlogPreviewEditor />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/blog/preview/:postId"
                element={
                  <ProtectedRoute requireRole="editor">
                    <BlogPreviewEditor />
                  </ProtectedRoute>
                }
              />

              {/* Dynamic service pages route - must be last to avoid conflicts */}
              <Route path="/:serviceName" element={<DynamicServicePage />} />
            </Routes>
          </Suspense>
        </EditModeProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
